/**
 * Clause confidence composition (plan §6.1): multiplicative, documented, exact.
 *
 * @remarks
 * Start at 1.0 per clause and multiply per evidence of interpretation
 * distance: ×0.97 for an alias-matched intent, ×0.97 per alias-matched entity
 * head, ×0.9 per pronoun resolution, and — once M5 wires corrections — ×0.9
 * per distance-1 or ×0.8 per distance-2 fuzzy-corrected token.
 *
 * Direction tokens carry NO multiplier: §6.1 weighs intents and nouns only,
 * and authored direction shorthand (`n` → `north`) expresses equivalence, not
 * doubt (design discovery, M4 — supersedes a stray M3 comment that claimed
 * otherwise).
 *
 * No randomness anywhere: identical evidence ⇒ identical confidence, byte for
 * byte. The floor check (outcomes below `confidenceFloor` become
 * `unrecognized`) is the orchestrator's, not this module's.
 *
 * @packageDocumentation
 */

import { type TokenCorrection } from '../types.js';

/** ×-multiplier for each alias-matched intent or entity head (plan §6.1). */
export const CONFIDENCE_ALIAS_MULTIPLIER = 0.97;

/** ×-multiplier per distance-1 fuzzy correction (plan §6.1; applied from M5). */
export const CONFIDENCE_CORRECTION_DISTANCE_ONE_MULTIPLIER = 0.9;

/** ×-multiplier per distance-2 fuzzy correction (plan §6.1; applied from M5). */
export const CONFIDENCE_CORRECTION_DISTANCE_TWO_MULTIPLIER = 0.8;

/** ×-multiplier per pronoun resolution (plan §6.1). */
export const CONFIDENCE_PRONOUN_MULTIPLIER = 0.9;

/** The interpretation-distance evidence one clause accumulated through the pipeline. */
export interface ClauseConfidenceEvidence {
	/** Fuzzy corrections applied to this clause's tokens (M5; empty until then). */
	readonly corrections: readonly TokenCorrection[];
	/** How many entity heads matched via alias rather than canonical noun. */
	readonly entityAliasMatchCount: number;
	/** How many pronouns resolved against recency. */
	readonly pronounResolutionCount: number;
	/** Whether the clause's intent surfaced through an alias (`grab`, `pick up` → `take`). */
	readonly wasIntentAliasMatched: boolean;
}

/**
 * Compose one clause's confidence from its accumulated evidence.
 *
 * @param evidence - What the pipeline observed while reading the clause
 * @returns The composed confidence in (0, 1]
 */
export const scoreParseCandidate = (evidence: ClauseConfidenceEvidence): number => {
	let confidence = 1;

	if (evidence.wasIntentAliasMatched) {
		confidence *= CONFIDENCE_ALIAS_MULTIPLIER;
	}

	confidence *= CONFIDENCE_ALIAS_MULTIPLIER ** evidence.entityAliasMatchCount;
	confidence *= CONFIDENCE_PRONOUN_MULTIPLIER ** evidence.pronounResolutionCount;

	for (const correction of evidence.corrections) {
		confidence *=
			correction.editDistance <= 1
				? CONFIDENCE_CORRECTION_DISTANCE_ONE_MULTIPLIER
				: CONFIDENCE_CORRECTION_DISTANCE_TWO_MULTIPLIER;
	}

	return confidence;
};
